/**
 * Org Invoice Requests Store Slice
 *
 * Zustand slice for managing organization invoice request state including fetching,
 * submitting, and canceling.
 *
 * Part of GW-1257 implementation.
 */

import {
  clearError as clearOpError,
  hasOperationLoaded,
  isOperationLoading,
  setError,
  setLoaded,
  setLoading,
} from '@gw/ui';

import { invoiceRequestsApi } from '@/api/invoice-requests';
import { safeGetStorageItem, STORAGE_KEYS } from '@/constants/storageKeys';
import { ORG_INVOICE_REQUESTS_OPERATIONS } from '@/stores/slices/orgInvoiceRequestsSlice.constants';

import type {
  CreateInvoiceRequestData,
  InvoiceRequest,
  ErrorStates,
  ErrorStatuses,
  LoadedTimestamps,
  LoadingStates,
} from '@gw/ui/types';
import type { StateCreator } from 'zustand';

// ============================================================================
// Slice Interface
// ============================================================================

export interface OrgInvoiceRequestsSlice {
  // State properties
  invoiceRequests: InvoiceRequest[];
  isLoading: LoadingStates;
  hasLoaded: LoadingStates;
  lastLoaded: LoadedTimestamps;
  errors: ErrorStates;
  errorStatuses: ErrorStatuses;

  // Organization actions
  fetchOrgInvoiceRequests: (orgId: string) => Promise<void>;
  submitInvoiceRequest: (data: CreateInvoiceRequestData) => Promise<InvoiceRequest>;

  // Organization cancel action
  cancelInvoiceRequest: (requestId: string) => Promise<void>;

  // Utility actions
  clearInvoiceRequestsError: (operation?: string) => void;
  checkIsLoading: (operation: string) => boolean;
  checkHasLoaded: (operation: string) => boolean;
}

// ============================================================================
// Slice Creator
// ============================================================================

export const createOrgInvoiceRequestsSlice: StateCreator<OrgInvoiceRequestsSlice> = (set, get) => ({
  // State initialization
  invoiceRequests: [],
  isLoading: {},
  hasLoaded: {},
  lastLoaded: {},
  errors: {},
  errorStatuses: {},

  // Fetch invoice requests for an organization
  fetchOrgInvoiceRequests: async (orgId: string) => {
    const operation = ORG_INVOICE_REQUESTS_OPERATIONS.FETCH_ORG;
    const state = get();
    set({ isLoading: setLoading(state.isLoading, operation, true) });

    try {
      const requests = await invoiceRequestsApi.getOrgInvoiceRequests(orgId);

      const currentState = get();
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        true
      );
      set({
        invoiceRequests: requests,
        isLoading: setLoading(currentState.isLoading, operation, false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errors: clearOpError(currentState.errors, operation),
      });
    } catch (error) {
      const currentState = get();
      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        errors: setError(
          currentState.errors,
          operation,
          error instanceof Error ? error.message : String(error)
        ),
      });
      throw error;
    }
  },

  // Submit a new invoice request
  submitInvoiceRequest: async (data: CreateInvoiceRequestData) => {
    const operation = ORG_INVOICE_REQUESTS_OPERATIONS.SUBMIT;
    const state = get();
    set({ isLoading: setLoading(state.isLoading, operation, true) });

    try {
      // Get current org from localStorage with safe access
      const orgId = safeGetStorageItem(STORAGE_KEYS.CURRENT_ORG_ID);

      if (!orgId) {
        throw new Error('Organization ID not found. Please select an organization.');
      }

      const request = await invoiceRequestsApi.submitInvoiceRequest(orgId, data);

      const currentState = get();
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        true
      );
      set({
        invoiceRequests: [...currentState.invoiceRequests, request],
        isLoading: setLoading(currentState.isLoading, operation, false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errors: clearOpError(currentState.errors, operation),
      });

      return request;
    } catch (error) {
      const currentState = get();
      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        errors: setError(
          currentState.errors,
          operation,
          error instanceof Error ? error.message : String(error)
        ),
      });
      throw error;
    }
  },

  // Cancel an invoice request (organization action)
  cancelInvoiceRequest: async (requestId: string) => {
    const operation = ORG_INVOICE_REQUESTS_OPERATIONS.CANCEL;
    const state = get();
    set({ isLoading: setLoading(state.isLoading, operation, true) });

    try {
      const orgId = safeGetStorageItem(STORAGE_KEYS.CURRENT_ORG_ID);

      if (!orgId) {
        throw new Error('Organization ID not found. Please select an organization.');
      }

      await invoiceRequestsApi.cancelInvoiceRequest(orgId, requestId);

      const currentState = get();
      const loadedState = setLoaded(
        currentState.hasLoaded,
        currentState.lastLoaded,
        operation,
        true
      );
      // Update the request's status to 'cancelled' in the local array
      set({
        invoiceRequests: currentState.invoiceRequests.map((r) =>
          r.id === requestId ? { ...r, status: 'cancelled' as const } : r
        ),
        isLoading: setLoading(currentState.isLoading, operation, false),
        hasLoaded: loadedState.hasLoaded,
        lastLoaded: loadedState.lastLoaded,
        errors: clearOpError(currentState.errors, operation),
      });
    } catch (error) {
      const currentState = get();
      set({
        isLoading: setLoading(currentState.isLoading, operation, false),
        errors: setError(
          currentState.errors,
          operation,
          error instanceof Error ? error.message : String(error)
        ),
      });
      throw error;
    }
  },

  // Utility actions
  clearInvoiceRequestsError: (operation?: string) => {
    const currentState = get();
    if (operation) {
      set({ errors: clearOpError(currentState.errors, operation) });
    } else {
      set({ errors: {} });
    }
  },
  checkIsLoading: (operation: string) => isOperationLoading(get().isLoading, operation),
  checkHasLoaded: (operation: string) => hasOperationLoaded(get().hasLoaded, operation),
});
